import * as i18next from 'i18next'
import { FontSizes, loadTheme } from 'office-ui-fabric-react'
import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from 'office-ui-fabric-react/lib/Checkbox'
import { ChoiceGroup } from 'office-ui-fabric-react/lib/ChoiceGroup'
import { DatePicker } from 'office-ui-fabric-react/lib/DatePicker'
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown'
import { Label } from 'office-ui-fabric-react/lib/Label'
import { IStackStyles, Stack } from 'office-ui-fabric-react/lib/Stack'
import { Text } from 'office-ui-fabric-react/lib/Text'
import { ITextFieldStyleProps, ITextFieldStyles, TextField } from 'office-ui-fabric-react/lib/TextField'
import * as React from 'react'
import { AsyncState } from 'widgets-for-react'
import { pushDatalayer } from '../datalayer'
import { InnerHtmlWrapper } from '../utils/InnerHtmlWrapper'
import { postFormSubmissionState } from './api'
import { status_error_renderer, status_sending_renderer, status_thank_you_renderer } from './status_message_renderers'
import { DependenciesState, DependencyData, FieldData, FormData, FormState, StepData } from './types'
import {
  are_all_dependencies_met,
  are_dependencies_met,
  DayPickerStrings,
  is_dependency_met,
  sortBy,
  update_elem_in_list,
  update_field_AUX,
  update_field_file
} from './utils'
import { RecaptchaV2 } from '../public_site/utils/recaptchaV2'
import moment from 'moment'

loadTheme({
  palette: {
    themePrimary: '#253a79',
    themeLighterAlt: '#f3f4fa',
    themeLighter: '#d0d6e9',
    themeLight: '#aab5d6',
    themeTertiary: '#6678ae',
    themeSecondary: '#354a88',
    themeDarkAlt: '#21346c',
    themeDark: '#1c2c5b',
    themeDarker: '#152043',
    neutralLighterAlt: '#e5e6ec',
    neutralLighter: '#e1e3e8',
    neutralLight: '#d8d9de',
    neutralQuaternaryAlt: '#c9cacf',
    neutralQuaternary: '#c0c1c6',
    neutralTertiaryAlt: '#b8babe',
    neutralTertiary: '#aab5d6',
    neutralSecondary: '#6678ae',
    neutralPrimaryAlt: '#354a88',
    neutralPrimary: '#253a79',
    neutralDark: '#1c2c5b',
    black: '#7383B2',
    white: '#FFF'
  },
  defaultFontStyle: {
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: '400'
  },
  fonts: {
    small: {
      fontSize: '12px'
    },
    medium: {
      fontSize: '24px'
    }
  }
})

const debug = false

export let show_optional = (input: boolean) => {
  return input ? '' : ' form__label--optional'
}

export let dependency_renderer = (dependency: DependencyData, formState: FormState) => (
  <Stack horizontal horizontalAlign={'start'} gap={5}>
    <Text variant={'medium'}>
      <b>Title</b>: {dependency.dependency.title}
    </Text>
    <Text variant={'medium'}>
      <b>Is met?</b> {`${is_dependency_met(dependency.dependency, formState)}`}
    </Text>
    <Text variant={'medium'}>
      <b>Hide if violated?</b> {`${dependency.dependency.hideIfViolated}`}
    </Text>
    <Text variant={'medium'}>
      <b>Kind</b>: {dependency.dependency.Kind}
    </Text>
    <Text variant={'medium'}>
      <b>Dependent fields</b>: {dependency.dependent_fields.map((df) => `${df.FieldId}`).join(',')}
    </Text>
  </Stack>
)

export const stackStyles: IStackStyles = { root: { width: `100%` } }
export const stackStyles1: IStackStyles = { root: { width: `100%` } }
const textFieldStyles = (props: ITextFieldStyleProps): Partial<ITextFieldStyles> => ({
  ...(props.focused && {
    outline: 'none',
    fieldGroup: {
      outlineColor: '#FF7802',
      borderColor: '#FF7802'
    },
    field: {
      ...(props.multiline
        ? {}
        : {
            borderBottom: '1px solid #FF7802'
          })
    }
  }),
  fieldGroup: {
    transition: 'all .3s',
    ...(props.multiline
      ? {
          border: '1px solid #B2C3F4',
          minHeight: 215,
          padding: 10,
          backgroundColor: '#e9f4fd',
          outline: 'none',
          ...(props.focused && {
            borderColor: 'red'
          })
        }
      : {
          height: 60,
          padding: 0
        })
  },
  field: {
    padding: '10px 0',
    fontSize: 24,
    ...(props.multiline
      ? {}
      : {
          border: 'none',
          borderBottom: '1px solid #B2C3F4',
          transition: 'all .3s'
        })
  },
  errorMessage: {
    fontSize: FontSizes.small,
    color: 'red',
    paddingTop: 10
  },

  subComponentStyles: {
    label: {
      fontSize: FontSizes.small,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)'
    }
  }
})
const dropdownStyles = {
  caretDownWrapper: {
    right: 'unset',
    left: '0px',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  caretDown: {
    color: '#253A79'
  },
  title: {
    padding: '10px 10px 10px 35px',
    height: 60,
    border: 'none',
    color: '#253A79',
    background: '#e9f4fd',
    fontSize: 24,
    outline: 'none !important',
    transition: 'all .3s'
  },
  dropdownItemsWrapper: {
    padding: '10px 10px 10px 35px',
    background: '#e9f4fd'
  },
  dropdown: {
    border: 'none',
    borderBottom: '1px solid rgb(37 58 121 / 20%)',
    fontSize: 16,
    backgroundColor: '#e9f4fd'
  },
  dropdownOptionText: {
    fontSize: 24
  },
  errorMessage: {
    fontSize: FontSizes.small,
    color: 'red',
    paddingTop: 10
  }
}

const checkboxStyles = (props: ICheckboxStyleProps): Partial<ICheckboxStyles> => ({
  text: {
    fontSize: 18,
    lineHeight: 24,
    color: '#253A79'
  },
  checkmark: {
    color: '#FFF',
    fontSize: 16
  },
  checkbox: {
    borderColor: '#B2C3F4',
    transition: 'all .3s'
  }
})

const checkboxesStyles = (props: ICheckboxStyleProps): Partial<ICheckboxStyles> => ({
  text: {
    fontSize: 18,
    lineHeight: 24,
    color: '#253A79'
  },
  checkbox: {
    borderColor: '#B2C3F4',
    transition: 'all .3s'
  },
  checkmark: {
    fontSize: 15,
    color: '#FFF'
  }
})

export let field_renderer = (
  field: FieldData,
  formState: FormState,
  update_field: (updated_field: FieldData, updatedDependencyState: DependenciesState) => void
) => {
  let isRequired = false
  if (formState.form_data.kind == 'loaded') {
    formState.form_data.value.steps.forEach((s) =>
      s.dependencies.forEach((d) =>
        d.dependent_fields.forEach((f) => {
          if (f.FieldId == field.Id) {
            isRequired = true
          }
        })
      )
    )
  }
  let are_dependencies_met_res = are_dependencies_met(field, formState)
  let default_selected_answer = field.maybe_answers.find((a) => a.isDefaultSelected)
  switch (field.Kind) {
    case 'RadioOption':
      return (
        <div
          className={`radio-btn form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          }`}
        >
          <Label
            id={`field-${field.Id}`}
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{
              fontSize: FontSizes.small,
              color: '#7383B2',
              textTransform: 'uppercase',
              letterSpacing: 2
            }}
          >
            {field.title}
            {isRequired ? '*' : ''}
          </Label>
          <div className="radio-btn__wrap">
            <ChoiceGroup
              defaultSelectedKey={default_selected_answer != undefined ? `answer-${default_selected_answer.Id}` : ''}
              options={field.maybe_answers.map((a) => ({
                key: `answer-${a.Id}`,
                text: a.value
              }))}
              onChange={(_, selected_answer) => {
                if (field.Kind != 'RadioOption') return
                let res = update_field_AUX(field, selected_answer.text, formState)
                update_field(res.fieldData, res.dependencyState)
              }}
            />
          </div>
        </div>
      )

    case 'Dropdown':
      return (
        <div
          onBlur={() => {
            if (field.user_input == undefined) {
              let res = update_field_AUX(field, '', formState)
              update_field(res.fieldData, res.dependencyState)
            }
          }}
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          } dropdown`}
        >
          <Label
            id={`field-${field.Id}`}
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{ fontSize: FontSizes.small, color: '#7383B2', textTransform: 'uppercase', letterSpacing: 2 }}
          >
            {field.title}
            {isRequired ? '*' : ''}
          </Label>
          <Dropdown
            className="dropdown"
            placeholder={i18next.t('Form:make_a_choice')}
            options={
              !field.useExternalSource
                ? sortBy(
                    (a) => a.text,
                    field.maybe_answers.map((a) => ({
                      key: `answer-${a.Id}`,
                      text: a.label || a.value,
                      value: a.value,
                      class: `dropdown__select`
                    }))
                  )
                : !formState.context.external_options.has(field.sourceName)
                ? []
                : sortBy(
                    (a) => a.text,
                    formState.context.external_options.get(field.sourceName).map((a) => ({
                      key: `answer-${a}`,
                      text: a,
                      value: a,
                      class: `dropdown__select`
                    }))
                  )
            }
            onChange={(_, selected_answer: any) => {
              if (field.Kind != 'Dropdown') return
              let res = update_field_AUX(field, selected_answer.value, formState)
              update_field(res.fieldData, res.dependencyState)
            }}
            errorMessage={
              field.user_input != undefined && are_dependencies_met_res.kind == 'no'
                ? are_dependencies_met_res.messages.join(' | ')
                : ''
            }
            styles={dropdownStyles}
          />
        </div>
      )

    case 'Checkbox':
      return (
        <Stack
          onBlur={() => {
            if (field.user_input == undefined) {
              let res = update_field_AUX(field, '', formState)
              update_field(res.fieldData, res.dependencyState)
            }
          }}
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          }
            checkbox${
              field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
            }`}
        >
          <div className="checkbox__wrap">
            <Checkbox
              label={field.title + (isRequired ? ' *' : '')}
              defaultChecked={field.optionalDefaultValue.toLowerCase() == 'true'}
              onChange={(_, checked) => {
                let res = update_field_AUX(field, `${checked ? true : ''}`.toLowerCase(), formState)
                update_field(res.fieldData, res.dependencyState)
              }}
              styles={checkboxStyles}
            ></Checkbox>
            {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
              <div className="alert">
                <p className="ms-TextField-errorMessage error-message">
                  {are_dependencies_met_res.messages.join(' | ')}
                </p>
              </div>
            ) : null}
          </div>
        </Stack>
      )

    case 'CheckboxMultiSelect':
      return (
        <div
          onBlur={() => {
            if (field.user_input == undefined) {
              let res = update_field_AUX(field, '', formState)
              update_field(res.fieldData, res.dependencyState)
            }
          }}
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          }
            checkboxex${
              field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
            }`}
        >
          <Label
            id={`field-${field.Id}`}
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{
              fontSize: FontSizes.small,
              color: '#7383B2',
              textTransform: 'uppercase',
              letterSpacing: 2,
              paddingBottom: 20
            }}
          >
            {field.title}
            {isRequired ? '*' : ''}
          </Label>
          <div className="checkboxes__wrap">
            {field.maybe_answers.map((a) => {
              return (
                <div className="checkboxes__item">
                  <Checkbox
                    label={a.value}
                    styles={checkboxesStyles}
                    defaultChecked={a.isDefaultSelected}
                    onChange={(_, selected_answer) => {
                      if (field.Kind != 'CheckboxMultiSelect') return
                      let res = null
                      if (selected_answer) {
                        res = update_field_AUX(field, field.user_input + '[' + a.value + ']', formState)
                      } else {
                        res = update_field_AUX(field, field.user_input.replace('[' + a.value + ']', ''), formState)
                      }

                      update_field(res.fieldData, res.dependencyState)
                    }}
                  />
                </div>
              )
            })}
          </div>
          {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">{are_dependencies_met_res.messages.join(' | ')}</p>
            </div>
          ) : null}
        </div>
      )

    case 'SimpleDate':
      return (
        <Stack
          className={`form__group text-input${
            field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
          }`}
        >
          <Label
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{
              fontSize: FontSizes.small,
              color: '#7383B2',
              textTransform: 'uppercase',
              letterSpacing: 2
            }}
          >
            {field.title} {isRequired ? '*' : ''}
          </Label>
          <DatePicker
            textField={{ height: 60 }}
            firstDayOfWeek={1}
            strings={DayPickerStrings}
            placeholder={field.placeholder != undefined ? field.placeholder : ''}
            ariaLabel="Select a date"
            value={field.user_input === '' ? undefined : new Date(field.user_input)}
            minDate={
              (field.StartDate as any) == ''
                ? null
                : typeof field.StartDate == 'string'
                ? moment(field.StartDate).toDate()
                : field.StartDate?.toDate()
            }
            isMonthPickerVisible={false}
            onSelectDate={(new_date) => {
              let res = update_field_AUX(field, moment(new_date, 'DD-MM-YYYY').format(), formState)
              update_field(res.fieldData, res.dependencyState)
            }}
          />
          {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">{are_dependencies_met_res.messages.join(' | ')}</p>
            </div>
          ) : null}
        </Stack>
      )
    case 'Attachement':
      let allowed_extensions = '.pdf, .docx, .doc'
      return (
        <Stack
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          } text-input`}
        >
          <Label
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{
              fontSize: FontSizes.small,
              color: '#7383B2',
              textTransform: 'uppercase',
              letterSpacing: 2
            }}
          >
            {field.title}
            {isRequired ? '*' : ''}
          </Label>
          {
            <input
              placeholder={field.placeholder != undefined ? field.placeholder : ''}
              className={`form__input ${
                field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__input--error' : ''
              }`}
              type="file"
              accept={allowed_extensions}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                let files: FileList = (e.target as any).files
                let file = files[0]
                let extension =
                  '.' + (file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length) || file.name)
                if (!allowed_extensions.includes(extension)) {
                  window.alert(i18next.t('Form:only_pdf'))
                  e.preventDefault()
                  let res = update_field_file(field, null, '', formState)
                  update_field(res.fieldData, res.dependencyState)
                  return
                }
                let res = update_field_file(field, files[0], files[0].name, formState)
                update_field(res.fieldData, res.dependencyState)
              }}
            />
          }
          {field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? (
            <div className="alert">
              <p className="ms-TextField-errorMessage error-message">
                {are_dependencies_met_res.messages
                  .join(' | ')
                  .concat(field.user_input == '' && ` | ${i18next.t('Form:only_pdf')}`)}
              </p>
            </div>
          ) : null}
        </Stack>
      )
    case 'ReadonlyText':
      return (
        <Stack
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          } text--styled`}
        >
          {/* <div dangerouslySetInnerHTML={{ __html: parseParagraphs(field.Description) }} /> */}
          <InnerHtmlWrapper text={field.Description} />
        </Stack>
      )
    default:
      return (
        <Stack
          className={`form__group form__group--${
            field.width_percentage == 'Extra small'
              ? 'xs'
              : field.width_percentage == 'Small'
              ? 's'
              : field.width_percentage == 'Medium'
              ? 'm'
              : 'l'
          } text-input${
            field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__group--error' : ''
          }`}
          onBlur={() => {
            if (field.user_input == undefined) {
              let res = update_field_AUX(field, '', formState)
              update_field(res.fieldData, res.dependencyState)
            }
          }}
        >
          <Label
            className={`h3 form__label${show_optional(isRequired)}`}
            style={{
              fontSize: FontSizes.small,
              color: '#7383B2',
              textTransform: 'uppercase',
              letterSpacing: 2,
              ...(field.Kind == 'TextArea' && {
                paddingBottom: 20
              })
            }}
          >
            {field.title} {isRequired ? '*' : ''}
          </Label>
          <TextField
            key={`field-${field.Id}`}
            type={field.Kind == 'UserEmail' ? 'email' : field.Kind == 'Phone' ? 'tel' : ''}
            placeholder={field.placeholder != undefined ? field.placeholder : ''}
            ariaLabel={`${isRequired ? 'Required ' : ''}${field.title} `}
            borderless={field.Kind != 'TextArea'}
            multiline={field.Kind == 'TextArea'}
            autoAdjustHeight={true}
            rows={field.Kind == 'TextArea' ? 2 : null}
            className={`form__input ${
              field.user_input != undefined && are_dependencies_met_res.kind == 'no' ? ' form__input--error' : ''
            }`}
            defaultValue={`${field.user_input ? field.user_input : ''}`}
            onChange={(e, text) => {
              let res = update_field_AUX(field, text, formState)
              update_field(res.fieldData, res.dependencyState)
            }}
            style={{
              background: '#e9f4fd',
              outline: 'none',
              ...(field.Kind == 'TextArea'
                ? {
                    borderBottom: '1px solid rgb(37 58 121 / 20%)'
                  }
                : {})
            }}
            styles={textFieldStyles}
            errorMessage={`${
              field.user_input != undefined && are_dependencies_met_res.kind == 'no'
                ? are_dependencies_met_res.messages.join(' | ')
                : ''
            }`}
          />
        </Stack>
      )
  }
}

export let step_renderer = (
  step: StepData,
  formState: FormState,
  update_step: (updated_step: StepData, updatedDependencyState: DependenciesState) => void
) => (
  <Stack gap={20}>
    <Stack gap={10} className="form__wrap" horizontal wrap verticalAlign="start" style={{ width: '100%' }}>
      {step.fields
        .sort((a, b) => (a.itemOrderNumber < b.itemOrderNumber ? -1 : a.itemOrderNumber > b.itemOrderNumber ? 1 : 0))
        .map((field, i) =>
          field_renderer(field, formState, (field1, updated_dependencies) =>
            update_step({ ...step, fields: update_elem_in_list(step.fields, i, field1) }, updated_dependencies)
          )
        )}
    </Stack>
    {debug ? (
      <Stack>
        <Text variant={'large'}> Dependencies: </Text>
        <Stack gap={10}>{step.dependencies.map((dependency) => dependency_renderer(dependency, formState))}</Stack>
      </Stack>
    ) : null}
  </Stack>
)

export const form_renderer = (
  data: FormData,
  formState: FormState,
  update_form_submission_logic: (updated_form_submission_logic: AsyncState<string>) => void,
  update_form: (updated_form: FormData, updatedDependencyState: DependenciesState) => void,
  set_recaptcha_token: (token: string) => void
) => {
  const are_dependencies_met = are_all_dependencies_met(formState)
  const recaptchaDone = !!formState.recaptcha_token
  return (
    <Stack
      as="form"
      styles={stackStyles}
      className="form"
      onSubmit={(e) => {
        e.preventDefault()
        pushDatalayer({
          category: data.form.title,
          action: 'Submit form',
        })
        if (formState.alternative_submit_api) {
          update_form_submission_logic(formState.alternative_submit_api(data.form.Id, formState))
        } else {
          update_form_submission_logic(postFormSubmissionState(data.form.Id, formState))
        }
      }}
    >
      <Stack className="form__inner" verticalAlign="start" horizontalAlign="start">
        {formState.form_submission_logic.kind == 'loading' && status_sending_renderer()}
        {formState.form_submission_logic.kind == 'loaded' &&
          formState.form_submission_logic.value == 'ok' &&
          formState.form_data.kind == 'loaded' &&
          status_thank_you_renderer(formState.form_data.value.thank_you.content)}
        {formState.form_submission_logic.kind == 'connection-error' &&
          formState.form_submission_logic.retryCount >= 10 &&
          status_error_renderer()}
        {formState.form_submission_logic.kind == 'loaded' &&
        formState.form_submission_logic.value == 'ok' &&
        formState.form_data.kind == 'loaded' ? null : (
          <>
            <Stack className="form__content" gap={20} style={{ width: '100%' }}>
              {data.steps
                .sort((a, b) =>
                  a.step.orderNumber < b.step.orderNumber ? -1 : a.step.orderNumber > b.step.orderNumber ? 1 : 0
                )
                .map((step, i) =>
                  step_renderer(step, formState, (step1, updated_dependencies) =>
                    update_form({ ...data, steps: update_elem_in_list(data.steps, i, step1) }, updated_dependencies)
                  )
                )}
            </Stack>
            {/*Sometimes the form is so big that you don't see the message ate the top. So also place this at the bottom of the form */}
            <RecaptchaV2
              onVerify={(token) => {
                console.log('receivedToken', token)
                if (formState.form_data.kind === 'loaded') {
                  set_recaptcha_token(token)
                }
              }}
            />
            <button
              // we disable the form when the submission is being send
              disabled={!are_dependencies_met || formState.form_submission_logic.kind == 'loading' || !recaptchaDone}
              type="submit"
              className="form__button outline-button"
            >
              {i18next.t('Form:form_submit')}
            </button>
          </>
        )}
      </Stack>
    </Stack>
  )
}
